import React from 'react'

/**
 * Global Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs the error, and displays a fallback UI.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console (could also send to error tracking service)
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({ errorInfo })

    // Optional: Send to error tracking service
    // logErrorToService(error, errorInfo)
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen bg-bg-darkest flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-bg-panel border border-border-light rounded-lg shadow-xl overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-accent-red/20 to-transparent border-b border-border-light">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent-red/20 border border-accent-red/40 flex items-center justify-center">
                  <span className="text-xl">⚠️</span>
                </div>
                <div>
                  <h2 className="text-text-primary font-bold text-lg">Something went wrong</h2>
                  <p className="text-xs text-text-muted">An unexpected error occurred</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <p className="text-text-secondary text-sm">
                We're sorry, but something unexpected happened. You can try refreshing the page
                or going back to the previous screen.
              </p>

              {/* Error details (development only) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="bg-bg-darker border border-border-dark rounded-md p-3 overflow-auto max-h-40">
                  <p className="text-accent-red text-xs font-mono break-all">
                    {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo && (
                    <pre className="text-text-muted text-xs mt-2 whitespace-pre-wrap">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={this.handleRetry}
                  className="flex-1 px-4 py-2 bg-bg-darker border border-border-light rounded-md text-text-primary text-sm font-medium hover:bg-bg-hover hover:border-accent-blue transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={this.handleReload}
                  className="flex-1 px-4 py-2 bg-accent-blue text-white rounded-md text-sm font-medium hover:bg-accent-blue-light transition-colors"
                >
                  Reload Page
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 bg-bg-darker border-t border-border-light">
              <p className="text-text-muted text-xs text-center">
                If this problem persists, please{' '}
                <a
                  href="mailto:roee57@gmail.com?subject=Commonfolk%20Downfall%20Error%20Report&body=Describe%20the%20steps%20to%20reproduce%20the%20error%20here."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-blue hover:underline"
                >
                  report an issue
                </a>
              </p>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
